import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { jsPDF } from 'jspdf'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import { Progress } from 'components/ui/Progress'
import getDc3CertificateDownloadData, {
  mapDownloadResponseToCertificates,
} from 'api/administration/courses/GetDc3CertificateDownloadData'
import getUserDc3CertificateDownloadData from 'api/users/GetUserDc3CertificateDownloadData'
import { buildDc3DummyData } from './buildDc3DummyData'
import {
  buildDc3PdfFileName,
  downloadDc3Pdf,
  generateDc3Pdf,
  openDc3PdfInNewTab,
} from './generateDc3Pdf'
import { IDc3SignatureOverrides } from './resolveSignatureForPdf'
import { IDc3AttestationTemplateData, IDc3CertificateData } from './types'
import {
  Counter,
  Description,
  ModalContent,
  ProgressWrapper,
  SpinWrapper,
} from './Dc3CertificateGenerateModal.styled'

export type Dc3GenerationMode = 'preview' | 'download' | 'zip'

type GenerationErrorKind = 'no_participants' | 'generic'

interface PreviewConfig {
  attestationId?: number
  attestation?: Partial<IDc3AttestationTemplateData>
  signatureOverrides?: IDc3SignatureOverrides
  course?: Partial<{ name: string; hours: number }>
}

interface IProps {
  open: boolean
  mode: Dc3GenerationMode
  courseId?: number
  userIds?: number[]
  profileUserId?: number
  previewConfig?: PreviewConfig
  zipFileName?: string
  onClose: () => void
  onComplete?: () => void
  onError?: () => void
}

const Dc3CertificateGenerateModal: React.FC<IProps> = ({
  open,
  mode,
  courseId,
  userIds,
  profileUserId,
  previewConfig,
  zipFileName,
  onClose,
  onComplete,
  onError,
}) => {
  const { t } = useTranslation()
  const prefix = 'dc3Certificate.generateModal'

  const [loading, setLoading] = useState(false)
  const [certificates, setCertificates] = useState<IDc3CertificateData[]>([])
  const [generatedCount, setGeneratedCount] = useState(0)
  const [processing, setProcessing] = useState(false)
  const [errorKind, setErrorKind] = useState<GenerationErrorKind | null>(null)

  const previewConfigRef = useRef(previewConfig)
  previewConfigRef.current = previewConfig

  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  const generationRunIdRef = useRef(0)

  const total = certificates.length
  const percentage = total ? Math.round((generatedCount * 100) / total) : 0
  const isBusy = loading || (processing && !errorKind)

  const resetState = useCallback(() => {
    setLoading(false)
    setCertificates([])
    setGeneratedCount(0)
    setProcessing(false)
    setErrorKind(null)
  }, [])

  const handleClose = useCallback(() => {
    if (isBusy) return
    onClose()
  }, [isBusy, onClose])

  useEffect(() => {
    if (!open) {
      resetState()
      return undefined
    }

    const runId = ++generationRunIdRef.current
    let cancelled = false
    const isActive = () => !cancelled && runId === generationRunIdRef.current

    const run = async () => {
      setLoading(true)
      setProcessing(false)
      setGeneratedCount(0)
      setErrorKind(null)
      setCertificates([])

      try {
        let items: IDc3CertificateData[] = []

        if (mode === 'preview') {
          items = [
            buildDc3DummyData({
              attestation: previewConfigRef.current?.attestation,
              course: previewConfigRef.current?.course,
            }),
          ]
        } else {
          if (!courseId) {
            throw new Error('missing_course_id')
          }

          const response = profileUserId
            ? await getUserDc3CertificateDownloadData({
                userId: profileUserId,
                courseId,
              })
            : await getDc3CertificateDownloadData({
                courseId,
                userIds,
              })

          items = mapDownloadResponseToCertificates(response)

          if (!items.length) {
            throw new Error('no_participants')
          }
        }

        if (!isActive()) return

        setCertificates(items)
        setLoading(false)
        setProcessing(true)

        const generatedPdfs: jsPDF[] = []
        const previewSignatureOverrides =
          previewConfigRef.current?.signatureOverrides

        for (let index = 0; index < items.length; index += 1) {
          if (!isActive()) return

          const certificate = items[index]
          const pdf = await generateDc3Pdf(certificate, {
            signatureOverrides:
              mode === 'preview' ? previewSignatureOverrides : undefined,
            usePreviewPlaceholder: mode === 'preview',
            attestationId:
              mode === 'preview'
                ? previewConfigRef.current?.attestationId ??
                  certificate.attestation.id
                : certificate.attestation.id,
          })
          generatedPdfs.push(pdf)

          if (!isActive()) return

          setGeneratedCount(index + 1)

          if (mode === 'preview') {
            if (isActive()) {
              openDc3PdfInNewTab(pdf, t(`${prefix}.previewTitle`))
            }
            break
          }

          if (mode === 'download') {
            downloadDc3Pdf(
              pdf,
              buildDc3PdfFileName({ fullName: certificate.worker.fullName })
            )
            break
          }
        }

        if (!isActive()) return

        if (mode === 'zip') {
          const Zip = (await import('adm-zip')).default
          const { sanitizeZipEntryName } = await import(
            'helpers/sanitizeZipEntryName'
          )
          const zip = new Zip()

          generatedPdfs.forEach((pdf, index) => {
            const certificate = items[index]
            const arrayBuffer = pdf.output('arraybuffer')
            zip.addFile(
              sanitizeZipEntryName(
                buildDc3PdfFileName({ fullName: certificate.worker.fullName })
              ),
              Buffer.from(arrayBuffer)
            )
          })

          const buffer = await zip.toBufferPromise()
          const blob = new Blob([buffer], { type: 'application/zip' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download =
            zipFileName || t(`${prefix}.zipFileName`, { count: items.length })
          link.click()
          URL.revokeObjectURL(url)
        }

        if (!isActive()) return

        onCompleteRef.current?.()
        onCloseRef.current()
      } catch (caughtError) {
        if (!isActive()) return

        const isNoParticipants =
          caughtError instanceof Error &&
          caughtError.message === 'no_participants'

        setErrorKind(isNoParticipants ? 'no_participants' : 'generic')
        setProcessing(false)
        onErrorRef.current?.()
      } finally {
        if (isActive()) {
          setLoading(false)
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [courseId, mode, open, profileUserId, resetState, t, userIds, zipFileName])

  const title = useMemo(() => {
    if (errorKind === 'no_participants') {
      return t(`${prefix}.noEligibleTitle`)
    }
    if (mode === 'preview') return t(`${prefix}.previewTitle`)
    if (mode === 'zip') return t(`${prefix}.zipTitle`)
    return t(`${prefix}.downloadTitle`)
  }, [errorKind, mode, t])

  const description = useMemo(() => {
    if (errorKind === 'no_participants') {
      return mode === 'download'
        ? t(`${prefix}.noEligibleParticipant`)
        : t(`${prefix}.noEligibleParticipants`)
    }
    if (errorKind === 'generic') return t(`${prefix}.error`)
    if (loading) return t(`${prefix}.loading`)
    return t(`${prefix}.generating`)
  }, [errorKind, loading, mode, t])

  return (
    <Modal
      isOpen={open}
      title={title}
      isConfirmation={!!errorKind}
      maxwidth={520}
      maxheight={errorKind ? 420 : 320}
      close={handleClose}
      actions={
        errorKind ? (
          <ModalButtons
            secondaryText={t(`${prefix}.understood`)}
            secondaryAction={handleClose}
          />
        ) : undefined
      }
    >
      <ModalContent $alignLeft={!!errorKind}>
        {isBusy && (
          <SpinWrapper>
            <Spin />
          </SpinWrapper>
        )}
        <Description $alignLeft={!!errorKind}>{description}</Description>
        {!loading && !errorKind && total > 0 && (
          <>
            <Counter>
              {generatedCount} / {total}
            </Counter>
            <ProgressWrapper>
              <Progress percent={percentage} className="" />
            </ProgressWrapper>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default Dc3CertificateGenerateModal
